import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polygon, Marker, Popup, LayersControl, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, X, Check, Undo2, Trash2 } from "lucide-react";
import { num } from "../../lib/api";

const BAFRA_CENTER = [41.5686, 35.9067];
const DEFAULT_ZOOM = 13;

// Custom small pin icon
const pinIcon = L.divIcon({
  className: "rice-pin",
  html: '<span></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Spherical polygon area (m²) using shoelace on lat/lng
export function polygonAreaM2(latlngs) {
  if (!latlngs || latlngs.length < 3) return 0;
  const R = 6378137;
  let area = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const [lat1, lon1] = latlngs[i];
    const [lat2, lon2] = latlngs[(i + 1) % latlngs.length];
    area += ((lon2 - lon1) * Math.PI) / 180 * (2 + Math.sin((lat1 * Math.PI) / 180) + Math.sin((lat2 * Math.PI) / 180));
  }
  return Math.abs((area * R * R) / 2);
}
export const m2ToDonum = (m2) => m2 / 1000;

function polygonCenter(latlngs) {
  if (!latlngs.length) return null;
  const [lat, lng] = latlngs.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
  return [lat / latlngs.length, lng / latlngs.length];
}

function FitBounds({ paths }) {
  const map = useMap();
  useEffect(() => {
    const all = paths.flat();
    if (all.length >= 2) {
      const bounds = L.latLngBounds(all);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [paths, map]);
  return null;
}

function DrawCatcher({ enabled, onAddVertex }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onAddVertex([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function RiceMap({ fields, center, onCreatePolygon, onSelectField }) {
  const [drawing, setDrawing] = useState(false);
  const [vertices, setVertices] = useState([]);
  const mapCenter = center && center.length === 2 ? center : BAFRA_CENTER;

  const startDrawing = () => {
    setVertices([]);
    setDrawing(true);
  };
  const cancelDrawing = () => {
    setVertices([]);
    setDrawing(false);
  };
  const undoVertex = () => setVertices((v) => v.slice(0, -1));
  const finishDrawing = () => {
    if (vertices.length < 3) {
      alert("Bir tarla için en az 3 nokta gerekli.");
      return;
    }
    const areaM2 = polygonAreaM2(vertices);
    const donum = m2ToDonum(areaM2);
    const center = polygonCenter(vertices);
    onCreatePolygon({ path: vertices, area_donum: Number(donum.toFixed(2)), center });
    setDrawing(false);
    setVertices([]);
  };

  const allPaths = fields.filter((f) => Array.isArray(f.path) && f.path.length >= 3).map((f) => f.path);

  return (
    <div className="rice-map-wrap">
      <div className="rice-map-toolbar">
        {!drawing && (
          <button className="primary-btn" data-testid="map-draw-start" onClick={startDrawing}>
            <MapPin size={15} /> Haritadan tarla çiz
          </button>
        )}
        {drawing && (
          <>
            <span className="rice-map-hint">Haritada tıklayarak köşeleri işaretleyin ({vertices.length} nokta)</span>
            <div className="rice-map-drawbar">
              <button className="mini-btn" data-testid="map-draw-undo" onClick={undoVertex} disabled={!vertices.length}>
                <Undo2 size={13} /> Geri
              </button>
              <button className="mini-btn danger" data-testid="map-draw-cancel" onClick={cancelDrawing}>
                <X size={13} /> İptal
              </button>
              <button className="primary-btn" data-testid="map-draw-finish" onClick={finishDrawing} disabled={vertices.length < 3}>
                <Check size={14} /> Bitir ve kaydet
              </button>
            </div>
          </>
        )}
      </div>

      <MapContainer center={mapCenter} zoom={DEFAULT_ZOOM} className="rice-leaflet" scrollWheelZoom>
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Uydu">
            <TileLayer
              attribution='&copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Sokak">
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <FitBounds paths={allPaths.length ? allPaths : [[mapCenter, mapCenter]]} />

        {fields.map((f) => {
          if (Array.isArray(f.path) && f.path.length >= 3) {
            return (
              <Polygon
                key={f.id}
                positions={f.path}
                pathOptions={{ color: "#facc15", weight: 2, fillColor: "#facc15", fillOpacity: 0.25 }}
                eventHandlers={{ click: () => onSelectField && onSelectField(f) }}
              >
                <Popup>
                  <div className="rice-popup">
                    <b>{f.name}</b>
                    <div>{num(f.area_donum)} dönüm</div>
                    {f.yield_kg ? <div>Verim: {num(f.yield_kg)} kg</div> : null}
                  </div>
                </Popup>
              </Polygon>
            );
          }
          if (f.center && f.center.length === 2) {
            return (
              <Marker key={f.id} position={f.center} icon={pinIcon}>
                <Popup>
                  <b>{f.name}</b> — {num(f.area_donum)} dönüm
                </Popup>
              </Marker>
            );
          }
          return null;
        })}

        {drawing && vertices.length >= 3 && (
          <Polygon positions={vertices} pathOptions={{ color: "#22c55e", weight: 2, dashArray: "5,5", fillColor: "#22c55e", fillOpacity: 0.2 }} />
        )}
        {drawing && vertices.map((v, i) => (
          <Marker key={i} position={v} icon={pinIcon} />
        ))}

        <DrawCatcher enabled={drawing} onAddVertex={(v) => setVertices((prev) => [...prev, v])} />
      </MapContainer>

      {drawing && vertices.length >= 3 && (
        <div className="rice-map-preview" data-testid="map-draw-preview">
          <span>Anlık alan:</span>
          <b>{num(m2ToDonum(polygonAreaM2(vertices)))} dönüm</b>
        </div>
      )}
    </div>
  );
}
